import CartEntity from '@/entities/api/cart/cart'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
	NameAttributes,
	ValueType,
	ReferenceType,
	colorsMap,
	sizeMap,
	flavorMap,
	objectivesMap,
	modalitiesMap,
} from '@/entities/api/products/product'

interface CartSlice {
	cart: CartEntity[]
	isCartModalOpen: boolean
	totalItems: number
	totalPrice: number
	totalShipping: number
	grandTotal: number
}

interface AddToCartPayload {
	cartItem: CartEntity
}

interface UpdateCartPayload {
	productId: string
	attributeId: string
	quantity: number
	attributeValues?: ValueType[]
}

interface RemoveFromCartPayload {
	cartId: string
}

interface RemoveByAttributePayload {
	productId: string
	attributeId: string
	attributeValues?: ValueType[]
}

const validateAttributeArrays = (
	names: NameAttributes[],
	values: ValueType[],
	references: ReferenceType[]
): boolean => {
	if (names.length !== values.length || names.length !== references.length) {
		return false
	}

	for (let i = 0; i < names.length; i++) {
		const name = names[i]
		const value = values[i]
		const reference = references[i]

		let expectedReference: string | undefined

		switch (name) {
			case NameAttributes.COLOR:
				expectedReference = colorsMap[value as keyof typeof colorsMap]
				break
			case NameAttributes.SIZE:
				expectedReference = sizeMap[value as keyof typeof sizeMap]
				break
			case NameAttributes.FLAVOR:
				expectedReference = flavorMap[value as keyof typeof flavorMap]
				break
			case NameAttributes.OBJECTIVE:
				expectedReference = objectivesMap[value as keyof typeof objectivesMap]
				break
			case NameAttributes.MODALITY:
				expectedReference = modalitiesMap[value as keyof typeof modalitiesMap]
				break
		}

		if (expectedReference && expectedReference !== String(reference)) {
			console.warn(
				`Reference mismatch for ${name}: expected "${expectedReference}", got "${reference}"`
			)
		}
	}

	return true
}

const normalizeAttribute = (attribute: any) => {
	if (!attribute) return null

	const names = Array.isArray(attribute.name)
		? attribute.name
		: [attribute.name]
	const values = Array.isArray(attribute.value)
		? attribute.value
		: [attribute.value]
	const references = Array.isArray(attribute.reference)
		? attribute.reference
		: [attribute.reference]

	if (names.length !== values.length) {
		const valuesToPad = names.length - values.length
		for (let i = 0; i < valuesToPad; i++) {
			values.push(values[0] || '')
		}
	}

	if (names.length !== references.length) {
		for (let i = references.length; i < names.length; i++) {
			const name = names[i]
			const value = values[i]
			let referenceValue = String(value)

			switch (name) {
				case NameAttributes.COLOR:
					referenceValue =
						colorsMap[value as keyof typeof colorsMap] || String(value)
					break
				case NameAttributes.SIZE:
					referenceValue =
						sizeMap[value as keyof typeof sizeMap] || String(value)
					break
				case NameAttributes.FLAVOR:
					referenceValue =
						flavorMap[value as keyof typeof flavorMap] || String(value)
					break
				case NameAttributes.OBJECTIVE:
					referenceValue =
						objectivesMap[value as keyof typeof objectivesMap] || String(value)
					break
				case NameAttributes.MODALITY:
					referenceValue =
						modalitiesMap[value as keyof typeof modalitiesMap] || String(value)
					break
			}

			references.push(referenceValue)
		}
	}

	return {
		...attribute,
		name: names,
		value: values,
		reference: references,
	}
}

const calculateAttributePrice = (
	attribute: any,
	discount: number = 0
): number => {
	const basePrice = attribute.price || 0
	const discountAmount = (basePrice * discount) / 100
	return Math.max(0, basePrice - discountAmount)
}

const calculateShippingCost = (product: any, quantity: number = 1): number => {
	if (!product.shipping_info) return 0

	const shippingInfo = product.shipping_info

	if (!shippingInfo.isHaveShipping || shippingInfo.type === 'free') {
		return 0
	}

	if (shippingInfo.type === 'pay') {
		const baseShippingPrice = shippingInfo.price || 0

		if (baseShippingPrice === 0 || quantity <= 0) {
			return 0
		}

		if (quantity === 1) {
			return baseShippingPrice
		}

		const additionalQuantity = quantity - 1
		const additionalShippingCost = additionalQuantity * (baseShippingPrice * 0.5)

		return baseShippingPrice + additionalShippingCost
	}

	return 0
}

const calculateCartTotals = (cart: CartEntity[]) => {
	let totalItems = 0
	let totalPrice = 0
	let totalShipping = 0

	cart.forEach((item) => {
		try {
			if (item.product.attributes?.[0]) {
				const attribute = item.product.attributes[0]
				const normalizedAttribute = normalizeAttribute(attribute)

				if (!normalizedAttribute) return

				if (Array.isArray(normalizedAttribute.name)) {
					validateAttributeArrays(
						normalizedAttribute.name,
						normalizedAttribute.value,
						normalizedAttribute.reference
					)
				}

				const quantity = normalizedAttribute.quantity || 0
				const discount = item.product.discount || 0
				const price = calculateAttributePrice(normalizedAttribute, discount)
				const shippingCost = calculateShippingCost(item.product, quantity)

				totalItems += quantity
				totalPrice += price * quantity

				if (quantity > 0) {
					totalShipping += shippingCost
				}
			}
		} catch (error) {
			console.error('Error calculating totals for cart item:', item._id, error)

			if (item.product.attributes?.[0]) {
				const attribute = item.product.attributes[0]
				const quantity = attribute.quantity || 0
				const price = attribute.price || 0
				const discount = item.product.discount || 0

				totalItems += quantity
				const discountedPrice = price - (price * discount) / 100
				totalPrice += discountedPrice * quantity

				if (quantity > 0) {
					totalShipping += calculateShippingCost(item.product, quantity)
				}
			}
		}
	})

	const grandTotal = totalPrice + totalShipping

	return { totalItems, totalPrice, totalShipping, grandTotal }
}

const areAttributeCombinationsEqual = (
	combo1: { names: NameAttributes[]; values: ValueType[] },
	combo2: { names: NameAttributes[]; values: ValueType[] }
): boolean => {
	if (
		combo1.names.length !== combo2.names.length ||
		combo1.values.length !== combo2.values.length
	) {
		return false
	}

	const namesEqual = combo1.names.every(
		(name, index) => name === combo2.names[index]
	)
	const valuesEqual = combo1.values.every(
		(value, index) => String(value) === String(combo2.values[index])
	)

	return namesEqual && valuesEqual
}

const findCartItemByAttributes = (
	cart: CartEntity[],
	productId: string,
	targetAttributeId: string,
	attributeValues?: ValueType[]
): number => {
	return cart.findIndex((item) => {
		if (item.product._id !== productId) {
			return false
		}

		const itemAttribute = item.product.attributes?.[0]
		if (!itemAttribute) {
			return false
		}

		if (itemAttribute._id === targetAttributeId) {
			return true
		}

		if (attributeValues && attributeValues.length > 0) {
			const normalizedAttribute = normalizeAttribute(itemAttribute)
			if (!normalizedAttribute) return false

			const itemNames = normalizedAttribute.name
			const itemValues = normalizedAttribute.value

			return areAttributeCombinationsEqual(
				{ names: itemNames, values: itemValues },
				{ names: itemNames, values: attributeValues }
			)
		}

		return false
	})
}

const initialState: CartSlice = {
	cart: [],
	isCartModalOpen: false,
	totalItems: 0,
	totalPrice: 0,
	totalShipping: 0,
	grandTotal: 0,
}

const cartSlice = createSlice({
	name: 'cart',
	initialState,
	reducers: {
		addMultipleToCart: (
			state,
			action: PayloadAction<{ cartItems: CartEntity[] }>
		) => {
			const { cartItems } = action.payload

			try {
				cartItems.forEach((cartItem) => {
					const attribute = cartItem.product.attributes?.[0]
					if (!attribute) {
						console.error('Cart item missing attributes:', cartItem._id)
						return
					}

					const existingItemIndex = findCartItemByAttributes(
						state.cart,
						cartItem.product._id ?? '',
						attribute._id || '',
						Array.isArray(attribute.value) ? attribute.value : [attribute.value]
					)

					if (existingItemIndex >= 0) {
						const existingAttribute =
							state.cart[existingItemIndex].product.attributes?.[0]
						if (existingAttribute) {
							const currentQuantity = existingAttribute.quantity || 0
							const newQuantity = attribute.quantity || 0
							existingAttribute.quantity = currentQuantity + newQuantity
						}
					} else {
						state.cart.push(cartItem)
					}
				})

				const totals = calculateCartTotals(state.cart)
				state.totalItems = totals.totalItems
				state.totalPrice = totals.totalPrice
				state.totalShipping = totals.totalShipping
				state.grandTotal = totals.grandTotal
			} catch (error) {
				console.error('Error adding multiple items to cart:', error)
			}
		},
		addToCart: (state, action: PayloadAction<AddToCartPayload>) => {
			const { cartItem } = action.payload

			try {
				const attribute = cartItem.product.attributes?.[0]
				if (!attribute) {
					console.error('Cart item missing attributes')
					return
				}

				const existingItemIndex = findCartItemByAttributes(
					state.cart,
					cartItem.product._id ?? '',
					attribute._id || '',
					Array.isArray(attribute.value) ? attribute.value : [attribute.value]
				)

				if (existingItemIndex >= 0) {
					const existingAttribute =
						state.cart[existingItemIndex].product.attributes?.[0]
					if (existingAttribute) {
						const currentQuantity = existingAttribute.quantity || 0
						const newQuantity = attribute.quantity || 0
						existingAttribute.quantity = currentQuantity + newQuantity
					}
				} else {
					state.cart.push(cartItem)
				}

				const totals = calculateCartTotals(state.cart)
				state.totalItems = totals.totalItems
				state.totalPrice = totals.totalPrice
				state.totalShipping = totals.totalShipping
				state.grandTotal = totals.grandTotal
			} catch (error) {
				console.error('Error adding item to cart:', error)
			}
		},

		updateCartQuantity: (state, action: PayloadAction<UpdateCartPayload>) => {
			const { productId, attributeId, quantity, attributeValues } =
				action.payload

			try {
				const itemIndex = findCartItemByAttributes(
					state.cart,
					productId,
					attributeId,
					attributeValues
				)

				if (itemIndex >= 0) {
					if (quantity <= 0) {
						state.cart.splice(itemIndex, 1)
					} else {
						const itemAttribute = state.cart[itemIndex].product.attributes?.[0]
						if (itemAttribute) {
							itemAttribute.quantity = quantity
						}
					}

					const totals = calculateCartTotals(state.cart)
					state.totalItems = totals.totalItems
					state.totalPrice = totals.totalPrice
					state.totalShipping = totals.totalShipping
					state.grandTotal = totals.grandTotal
				}
			} catch (error) {
				console.error('Error updating cart quantity:', error)
			}
		},

		removeFromCart: (state, action: PayloadAction<RemoveFromCartPayload>) => {
			const { cartId } = action.payload

			try {
				state.cart = state.cart.filter((item) => item._id !== cartId)

				const totals = calculateCartTotals(state.cart)
				state.totalItems = totals.totalItems
				state.totalPrice = totals.totalPrice
				state.totalShipping = totals.totalShipping
				state.grandTotal = totals.grandTotal
			} catch (error) {
				console.error('Error removing item from cart:', error)
			}
		},

		removeFromCartByAttribute: (
			state,
			action: PayloadAction<RemoveByAttributePayload>
		) => {
			const { productId, attributeId, attributeValues } = action.payload

			try {
				const itemIndex = findCartItemByAttributes(
					state.cart,
					productId,
					attributeId,
					attributeValues
				)

				if (itemIndex >= 0) {
					state.cart.splice(itemIndex, 1)

					const totals = calculateCartTotals(state.cart)
					state.totalItems = totals.totalItems
					state.totalPrice = totals.totalPrice
					state.totalShipping = totals.totalShipping
					state.grandTotal = totals.grandTotal
				}
			} catch (error) {
				console.error('Error removing item by attribute:', error)
			}
		},

		clearCart: (state) => {
			state.cart = []
			state.totalItems = 0
			state.totalPrice = 0
			state.totalShipping = 0
			state.grandTotal = 0
		},

		toggleCartModal: (state) => {
			state.isCartModalOpen = !state.isCartModalOpen
		},

		openCartModal: (state) => {
			state.isCartModalOpen = true
		},

		closeCartModal: (state) => {
			state.isCartModalOpen = false
		},

		initializeCartTotals: (state) => {
			const totals = calculateCartTotals(state.cart)
			state.totalItems = totals.totalItems
			state.totalPrice = totals.totalPrice
			state.totalShipping = totals.totalShipping
			state.grandTotal = totals.grandTotal
		},

		validateCartItems: (state) => {
			const validItems = state.cart.filter((item) => {
				try {
					if (
						!item.product.attributes ||
						item.product.attributes.length === 0
					) {
						return false
					}

					const attribute = item.product.attributes[0]

					if (!attribute.price || !attribute.quantity) {
						return false
					}

					const normalized = normalizeAttribute(attribute)
					if (!normalized) return false

					if (Array.isArray(normalized.name)) {
						return validateAttributeArrays(
							normalized.name,
							normalized.value,
							normalized.reference
						)
					}

					return true
				} catch (error) {
					console.error('Error validating cart item:', error)
					return false
				}
			})

			const removedCount = state.cart.length - validItems.length

			if (removedCount > 0) {
				console.warn(`Removed ${removedCount} invalid items from cart`)
				state.cart = validItems

				const totals = calculateCartTotals(state.cart)
				state.totalItems = totals.totalItems
				state.totalPrice = totals.totalPrice
				state.totalShipping = totals.totalShipping
				state.grandTotal = totals.grandTotal
			}
		},
	},
})

export const {
	addMultipleToCart,
	addToCart,
	updateCartQuantity,
	removeFromCart,
	removeFromCartByAttribute,
	clearCart,
	toggleCartModal,
	openCartModal,
	closeCartModal,
	initializeCartTotals,
	validateCartItems,
} = cartSlice.actions

export default cartSlice.reducer

export const selectCartTotalWithShipping = (state: { cart: CartSlice }) =>
	state.cart.grandTotal
export const selectCartSubtotal = (state: { cart: CartSlice }) =>
	state.cart.totalPrice
export const selectCartShipping = (state: { cart: CartSlice }) =>
	state.cart.totalShipping
export const selectCartItemsCount = (state: { cart: CartSlice }) =>
	state.cart.totalItems
export const selectCartItems = (state: { cart: CartSlice }) => state.cart.cart